import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Row, Col } from 'reactstrap';
import BusinessInfoStep from '../../../../components/Forms/Wizard/steps/BusinessInfoStep';
import OwnerInfoStep from '../../../../components/Forms/Wizard/steps/OwnerInfoStep';
import BankInfoStep from '../../../../components/Forms/Wizard/steps/BankInfoStep';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import apiService from '../../../../service/apiService';
import merchantService from '../../../../service/merchantService.ts';
import { validateBusinessInfo, validateBankInfo } from '../../../../components/Forms/Wizard/validation';
import InputsValidations from '../../../../modules/InputsValidations.js';

const UpdateMerchantModal = ({ isOpen = false, toggle = () => {}, merchantId = null, onUpdated = () => {} }) => {
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [errors, setErrors] = React.useState({});

  const [businessInfo, setBusinessInfo] = React.useState({
    type: 'DEALER',
    legalName: '',
    dbaName: '',
    federalTaxId: '',
    industry: '',
    website: '',
    stateOfInc: '',
    businessStructure: '',
    dateEstablished: '',
    phoneNumber: ''
  });
  const [businessAddresses, setBusinessAddresses] = React.useState([]);
  const [businessAddressError, setBusinessAddressError] = React.useState('');
  const [owners, setOwners] = React.useState([]);
  const [bankInfo, setBankInfo] = React.useState({ banks: [], draft: {} });

  const [msgOpen, setMsgOpen] = React.useState(false);
  const [msgConfig, setMsgConfig] = React.useState({ title: '', message: '', confirmColor: 'primary', icon: 'ni ni-check-bold' });
  const openMsg = (config) => { setMsgConfig({ ...msgConfig, ...config }); setMsgOpen(true); };
  const closeMsg = () => setMsgOpen(false);

  const toIsoDate = (d) => {
    if (!d) return '';
    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
      const dt = new Date(d);
      if (!isNaN(dt.getTime())) {
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, '0');
        const day = String(dt.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      }
    } catch (_) {}
    return '';
  };

  React.useEffect(() => {
    if (!isOpen || !merchantId) return;
    const load = async () => {
      try {
        setLoading(true);
        const token = await apiService.getToken();
        const { data } = await merchantService.getMerchantById(token, merchantId);
        const m = data || {};
        const base = m.merchant || m;
        setBusinessInfo({
          type: (base.type || 'DEALER').toString().toUpperCase(),
          legalName: base.legalName || '',
          dbaName: base.dbaName || '',
          federalTaxId: base.federalTaxId || '',
          industry: base.industry || '',
          website: base.website || '',
          stateOfInc: base.stateOfInc || '',
          businessStructure: base.businessStructure || '',
          dateEstablished: toIsoDate(base.dateEstablished),
          phoneNumber: base.phoneNumber || ''
        });
        setBusinessAddresses((m.addresses || []).map((addr) => ({
          id: addr.id,
          addressType: addr.addressType || 'PHYSICAL',
          streetAddress: addr.street1 || addr.streetAddress || '',
          streetAddress2: addr.street2 || addr.streetAddress2 || addr.unit || '',
          city: addr.city || '',
          state: addr.state || '',
          zipCode: addr.zipCode || '',
          country: addr.country || 'USA',
          phoneNumber: addr.phoneNumber || ''
        })));
        setOwners((m.owners || []).map((o) => {
          const ow = o.owner || o;
          return {
            id: ow.id,
            firstName: ow.firstName,
            middleName: ow.middleName,
            lastName: ow.lastName,
            gender: ow.gender,
            birthDate: ow.birthDate,
            citizenship: ow.citizenship,
            ownershipPercentage: ow.ownershipPercentage,
            socialSecurityNumber: ow.socialSecurityNumber,
            addresses: (o.addresses || ow.addresses || []).map((a) => ({
              id: a.id,
              addressType: a.addressType || 'PHYSICAL',
              streetAddress: a.street1 || a.streetAddress || '',
              streetAddress2: a.street2 || a.streetAddress2 || '',
              city: a.city,
              state: a.state,
              zipCode: a.zipCode,
              country: a.country || 'USA',
              phoneNumber: a.phoneNumber || ''
            })),
            contacts: (o.contacts || []).map((c) => ({
              id: c.id,
              type: String(c.type || '').toUpperCase() === 'PHONE' ? 'Phone' : 'Email',
              value: c.value,
              primary: (typeof c.isPrimary === 'boolean' ? (c.isPrimary ? 'Yes' : 'No') : (String(c.primary || '').toLowerCase() === 'yes' ? 'Yes' : 'No'))
            }))
          };
        }));
        setBankInfo({
          banks: (m.banks || []).map((b) => ({
            id: b.id,
            type: b.type,
            bankName: b.bankName,
            accountName: b.accountName,
            accountNumber: b.number || b.accountNumber,
            routingNumber: b.routingNumber,
            bankAddress: b.bankAddress ?? '',
            bankCity: b.bankCity ?? '',
            bankState: b.bankState ?? '',
            bankCountry: b.bankCountry ?? 'USA',
          })),
          draft: {}
        });
        setErrors({});
        setBusinessAddressError('');
      } catch (e) {
        openMsg({ title: 'Error', message: 'Failed to load merchant details.', confirmColor: 'danger', icon: 'ni ni-fat-remove' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, merchantId]);

  const handleBusinessChange = (e) => {
    const { name, value } = e.target;
    let processed = value;
    // Reutilizar formateos del wizard
    if (name === 'federalTaxId') {
      processed = InputsValidations.formatFederalTaxId(value, businessInfo.federalTaxId);
    }
    if ((name === 'legalName' || name === 'dbaName') && value.length > 255) {
      return; // límite 255
    }
    setBusinessInfo(prev => ({ ...prev, [name]: processed }));
  };

  const buildPayload = () => {
    // Normalizar fecha establecida como en el wizard
    const normalizeDate = (d) => {
      if (!d) return undefined;
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
      try {
        const dt = new Date(d);
        if (!isNaN(dt.getTime())) {
          const y = dt.getFullYear();
          const m = String(dt.getMonth()+1).padStart(2,'0');
          const day = String(dt.getDate()).padStart(2,'0');
          return `${y}-${m}-${day}`;
        }
      } catch (_) {}
      return undefined;
    };
    const addressDtos = businessAddresses.map((addr) => ({
      ...(addr && addr.id ? { id: addr.id } : {}),
      addressType: addr.addressType || 'PHYSICAL',
      street1: addr.streetAddress,
      street2: addr.streetAddress2 || addr.unit || null,
      city: addr.city,
      state: addr.state,
      zipCode: addr.zipCode,
      country: addr.country || 'USA',
      phoneNumber: addr.phoneNumber || null
    }));
    const ownerDtos = owners.map((o) => ({
      ...(o && o.id ? { id: o.id } : {}),
      firstName: o.firstName,
      middleName: o.middleName,
      lastName: o.lastName,
      gender: (o.gender || 'OTHER').toString().toUpperCase(),
      birthDate: o.birthDate,
      citizenship: o.citizenship,
      ownershipPercentage: parseFloat(o.ownershipPercentage) || 0,
      socialSecurityNumber: o.socialSecurityNumber,
      contacts: (o.contacts || []).map((c) => ({
        ...(c && c.id ? { id: c.id } : {}),
        type: String(c.type || '').toUpperCase() === 'PHONE' ? 'PHONE' : 'EMAIL',
        value: c.value,
        isPrimary: typeof c.isPrimary === 'boolean' ? c.isPrimary : (String(c.primary || '').toLowerCase() === 'yes' || String(c.primary || '').toLowerCase() === 'true'),
      })),
      addresses: (o.addresses || []).map((a) => ({
        ...(a && a.id ? { id: a.id } : {}),
        addressType: a.addressType || 'PHYSICAL',
        street1: a.streetAddress || a.street1,
        street2: a.streetAddress2 || a.street2 || null,
        city: a.city,
        state: a.state,
        zipCode: a.zipCode,
        country: a.country || 'USA',
        phoneNumber: a.phoneNumber || null
      })),
    }));
    const banks = (bankInfo?.banks || []).map((b) => ({
      ...(b && b.id ? { id: b.id } : {}),
      type: b.type,
      bankName: b.bankName,
      accountName: b.accountName,
      number: b.accountNumber || b.number,
      routingNumber: b.routingNumber,
      bankAddress: b.bankAddress ?? '',
      bankCity: b.bankCity ?? '',
      bankState: b.bankState ?? '',
      bankCountry: b.bankCountry ?? 'USA',
    }));
    return {
      legalName: businessInfo.legalName,
      dbaName: businessInfo.dbaName || undefined,
      federalTaxId: businessInfo.federalTaxId,
      stateOfInc: businessInfo.stateOfInc,
      industry: businessInfo.industry,
      businessStructure: businessInfo.businessStructure,
      website: businessInfo.website || undefined,
      dateEstablished: normalizeDate(businessInfo.dateEstablished) || undefined,
      phoneNumber: businessInfo.phoneNumber || undefined,
      type: (businessInfo.type || 'DEALER'),
      addresses: addressDtos,
      owners: ownerDtos,
      banks,
    };
  };

  const handleSave = async () => {
    // Basic validations similar to wizard MERCHANT mode
    const bizErrors = validateBusinessInfo(businessInfo);
    if (businessAddresses.length === 0) {
      setBusinessAddressError('At least one address is required');
    }
    const hasOwner = owners.length > 0;
    const ownerHasContact = owners.some(o => (o.contacts || []).length > 0);
    const ownerHasAddress = owners.some(o => (o.addresses || []).length > 0);
    const bankValidation = validateBankInfo(bankInfo);
    const mergedErrors = { ...bizErrors, ...(bankValidation || {}) };
    setErrors(mergedErrors);
    if (Object.keys(bizErrors).length > 0 || businessAddresses.length === 0 || !hasOwner || !ownerHasContact || !ownerHasAddress) {
      openMsg({ title: 'Validation Error', message: 'Please complete required fields before saving.', confirmColor: 'danger', icon: 'ni ni-fat-remove' });
      return;
    }

    try {
      setSaving(true);
      const payload = buildPayload();
      await apiService.updateMerchant(merchantId, payload);
      openMsg({ title: 'Success', message: 'Merchant updated successfully!', confirmColor: 'success', icon: 'ni ni-check-bold' });
      onUpdated();
    } catch (err) {
      const backendMsg = err?.originalMessage || err?.message || 'Failed to update merchant.';
      openMsg({ title: 'Server Error', message: backendMsg, confirmColor: 'danger', icon: 'ni ni-fat-remove' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
        <ModalHeader toggle={toggle}>Update Merchant</ModalHeader>
        <ModalBody>
          {loading ? (
            <div className="text-center py-5"><Spinner color="primary" /><span className="ml-2">Loading merchant...</span></div>
          ) : (
            <>
              <Row>
                <Col md="12">
                  <BusinessInfoStep
                    value={businessInfo}
                    onChange={handleBusinessChange}
                    errors={errors}
                    addresses={businessAddresses}
                    onAddressesChange={(list) => { setBusinessAddresses(list); if (list.length > 0) setBusinessAddressError(''); }}
                    addressError={businessAddressError}
                    onClearAddressError={() => setBusinessAddressError('')}
                    mode={'MERCHANT'}
                    showDuplicates={false}
                  />
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <OwnerInfoStep owners={owners} onOwnersChange={setOwners} errors={errors} isUpdateModal={true} />
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <BankInfoStep value={bankInfo} onChange={setBankInfo} errors={errors.bank || {}} />
                </Col>
              </Row>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={saving}>Close</Button>
          <Button color="primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? (<Spinner size="sm" />) : 'Save Changes'}
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmationModal
        isOpen={msgOpen}
        toggle={closeMsg}
        title={msgConfig.title}
        message={msgConfig.message}
        confirmText={'OK'}
        confirmColor={msgConfig.confirmColor}
        icon={msgConfig.icon}
      />
    </>
  );
};

export default UpdateMerchantModal;


